import type { HelpStep } from "../../help/types";

/**
 * The controls present on EVERY page — navigation, the header icons, the AI assistant — written
 * once here and appended to each page guide by the registry, so no page guide repeats them.
 *
 * Desktop and mobile render different components for the same destination (the side navigation
 * and the bottom bar), but both carry the SAME anchor name; the runtime picks whichever copy is
 * visible. Pure data — no UI imports.
 */
export const SHELL_SECTION = "Around the app";

/**
 * `HelpStep.context` keys used below → how the reader reveals that part of the UI. The registry
 * merges these into every page guide's `contexts`, next to the steps.
 */
export const shellContexts: Record<string, string> = {
  "chat-panel":
    "Click the round chat button at the bottom right (on a phone: “Ask a question” in the bottom bar).",
  settings: "Click the gear icon in the top bar (on a phone: your picture at the top right).",
};

export const shellSteps: HelpStep[] = [
  {
    selector: '[data-help="nav-client-switcher"]',
    title: "Switch to another employer",
    body: "Shows the employer whose benefits you are looking at. If your account is linked to more than one employer, click it and pick another one from the list; the app switches to that employer's benefits and takes you back to the start page. While the AI assistant is still connecting, the switch is briefly unavailable.",
    section: SHELL_SECTION,
  },
  {
    selector: '[data-help="nav-benefits"]',
    title: "Go to your benefits",
    body: "Opens the Benefits page, where you see every package your employer offers, enrol in one and manage what you already have. Navigation stays greyed out until you have given your consent on the Profile page.",
    section: SHELL_SECTION,
  },
  {
    selector: '[data-help="nav-dashboard"]',
    title: "See what your benefits cost",
    body: "Opens the Dashboard: the totals of your active benefits, split into what your employer pays and what you pay for yourself and your dependents. On a computer the side navigation is hidden on the start page.",
    section: SHELL_SECTION,
  },
  {
    selector: '[data-help="nav-profile-details"]',
    title: "Open your profile",
    body: "Opens your profile with your personal details, consents and dependents. The “More” button next to it opens the same page on your documents. Your contact details and date of employment are listed underneath.",
    section: SHELL_SECTION,
  },
  {
    selector: '[data-help="nav-employer-documents"]',
    title: "Read your employer's documents",
    body: "Opens the Employer page with the documents your employer has shared with all employees, such as benefit rules or policy conditions. Available once you have given your consent.",
    section: SHELL_SECTION,
  },
  {
    selector: '[data-help="nav-privacy-notice"]',
    title: "Read the privacy notice",
    body: "Opens the privacy notice that applies in your employer's country in a new browser tab. It explains how your personal data and the data of your dependents is processed.",
    section: SHELL_SECTION,
  },
  {
    selector: '[data-help="nav-support"]',
    title: "Contact support",
    body: "Opens a new e-mail to the support team in your mail program. Describe what you tried to do and on which page, so the team can reproduce it.",
    section: SHELL_SECTION,
  },
  {
    selector: '[data-help="nav-sign-out"]',
    title: "Sign out",
    body: "Ends your session and signs you out of the app. Do this on a shared or public computer; anything not yet saved in an open form is lost.",
    section: SHELL_SECTION,
  },
  {
    selector: '[data-help="app-chat-toggle"]',
    title: "Ask the AI assistant",
    body: "Opens the assistant panel, where you can ask questions about your benefits in your own words. It is available once you have given your consent. On a phone it is the “Ask a question” button in the bottom bar.",
    section: SHELL_SECTION,
  },
  {
    selector: '[data-help="app-chat-message"]',
    title: "Type a question for the assistant",
    body: "Write your question (up to 400 characters) and press Enter or the send button; Shift+Enter starts a new line. The assistant answers for the person currently selected on the Benefits page — you by default. While it is answering, the button turns into a stop button. Answers are generated by AI and can be wrong, so check anything important in your documents.",
    section: SHELL_SECTION,
    context: "chat-panel",
  },
  {
    selector: '[data-help="app-user-manual"]',
    title: "Open your employer's user manual",
    body: "The book icon in the top bar opens the user manual your employer's benefit programme provides, as a document in a new browser tab. If no manual has been uploaded for your employer yet, the app says so in the notifications instead.",
    section: SHELL_SECTION,
  },
  {
    selector: '[data-help="app-notifications"]',
    title: "Read your notifications",
    body: "The bell opens the messages the app showed you during this visit — confirmations and errors — with their count on the bell's badge. Each message can be copied (useful when contacting support) or dismissed; the list is emptied when you reload the page.",
    section: SHELL_SECTION,
  },
  {
    selector: '[data-help="app-settings"]',
    title: "Open the settings",
    body: "Opens the app settings: language, colour theme, dark mode and whether benefit cards show pictures. On a phone your picture opens a panel that also holds the employer switch and the links from the side navigation.",
    section: SHELL_SECTION,
  },
  {
    selector: '[data-help="app-settings-language"]',
    title: "Change the language",
    body: "Pick the language the app is shown in. The choice is remembered in this browser for your next visit.",
    section: SHELL_SECTION,
    context: "settings",
  },
  {
    selector: '[data-help="app-settings-theme"]',
    title: "Change the colour theme",
    body: "Click a colour to repaint buttons and highlights in it; the first colour is the standard theme. The last button opens a dialog to pick any colour. The choice is remembered in this browser.",
    section: SHELL_SECTION,
    context: "settings",
  },
  {
    selector: '[data-help="app-settings-dark-mode"]',
    title: "Switch dark mode on or off",
    body: "Shows the app with light text on a dark background. Remembered in this browser. The switch just above it shows or hides the pictures on benefit cards for the rest of this visit.",
    section: SHELL_SECTION,
    context: "settings",
  },
];
